/**
 * Trusscore Material Estimator - Main Orchestrator
 *
 * Entry point for all material calculations.
 * Supports three calculation modes:
 * - CeilingOnly: Calculate ceiling materials only
 * - FullRoom: Calculate 4-wall room with same configuration
 * - IndividualWalls: Calculate custom wall configurations
 *
 * Ported from: doEstimate() in estimator.js with cleaner architecture
 */

import {
  EstimateInput,
  EstimateOutput,
  BillOfMaterials,
  SurfaceBreakdown,
  SurfaceMaterials,
  CostTotals,
  Currency,
  BOMSmoothingConfig,
  TranslatableNote,
} from '../types';
import { validateEstimateInput } from './validation';
import { calculateCeiling, calculateCeilingZone, CeilingResult } from './ceiling';
import {
  calculateFullRoomWalls,
  calculateMultipleWalls,
  wallResultToSurfaceBreakdown,
  WallResult,
} from './walls';
import {
  buildBillOfMaterials,
  calculateCostTotals,
  createEmptyBOM,
  FullRoomTrimConfig,
} from './consolidation';
import { smoothResults } from './smoothing';
import {
  DEFAULT_ORPHAN_THRESHOLD,
  DEFAULT_MAX_WASTE_INCREASE_PERCENT,
} from './constants';

/**
 * Main estimate function - orchestrates all calculations.
 *
 * This is the primary entry point for the calculation engine.
 *
 * @param input - Complete estimate input configuration
 * @param smoothingConfig - Optional BOM smoothing configuration
 * @returns Full estimate output with BOM, costs, and breakdown
 * @throws Error if input validation fails
 */
export function doEstimate(
  input: EstimateInput,
  smoothingConfig?: BOMSmoothingConfig
): EstimateOutput {
  // Validate input
  const validation = validateEstimateInput(input);
  if (!validation.isValid) {
    throw new Error(
      `Invalid input: ${validation.errors.map(e => e.message).join(', ')}`
    );
  }

  // System notes are recorded twice over: as English text (what gets stored and
  // what estimates saved before i18n already carry) and as a key + parameters
  // the UI and PDF render in the active language. addNote keeps the two from
  // drifting — there is no way to add one without the other.
  const notesI18n: TranslatableNote[] = [];
  const addNote = (key: string, text: string, params?: Record<string, string | number>) =>
    notesI18n.push({ key, text, params });
  const notes = () => notesI18n.map((n) => n.text);

  let ceilingResult: CeilingResult | null = null;
  let wallResults: WallResult[] = [];

  // --- CEILING CALCULATION ---
  if (input.includeCeiling && input.ceilingConfig?.enabled) {
    ceilingResult = calculateCeiling(
      input.ceilingZones,
      input.ceilingConfig,
      input.roomDimensions
    );

    if (input.ceilingZones && input.ceilingZones.length > 1) {
      addNote(
        'results:notes.multiZoneCeiling',
        `Multi-zone ceiling with ${input.ceilingZones.length} zones`,
        { count: input.ceilingZones.length },
      );

      // Add detailed notes about zone boundary H-trim
      if (ceilingResult && ceilingResult.zoneBoundaryDetails && ceilingResult.zoneBoundaryDetails.length > 0) {
        for (const detail of ceilingResult.zoneBoundaryDetails) {
          addNote(
            'results:notes.zoneBoundaryTrim',
            `Zone boundary trim: Added ${detail.hTrimQuantity}x Snap-In H-Trim (10ft) for ${detail.zoneName} boundary (longest edge: ${detail.longestEdgeFeet}ft)`,
            // zoneName may be the user's own label, so it is passed through
            // as a value rather than being part of the translated sentence.
            { count: detail.hTrimQuantity, zone: detail.zoneName, edge: detail.longestEdgeFeet },
          );
        }
        addNote(
          'results:notes.multiZoneTrim',
          'Multi-zone trim note: Both Snap-In J Trim (full perimeter) and Snap-In H-Trim (zone boundaries) are included to cover all possible zone configurations — interlocking panels, butting panels, or separate zones.',
        );
      }
    }
  }

  // --- WALL CALCULATIONS ---
  switch (input.calculationMode) {
    case 'CeilingOnly':
      addNote('results:notes.ceilingOnlyMode', 'Ceiling-only calculation mode');
      break;

    case 'FullRoom':
      if (input.fullRoomWallConfig) {
        wallResults = calculateFullRoomWalls(
          input.roomDimensions,
          input.fullRoomWallConfig,
          input.includeCeiling
        );
        addNote('results:notes.fullRoomMode', 'Full room calculation with 4 walls');
      }
      break;

    case 'IndividualWalls':
      if (input.individualWalls && input.individualWalls.length > 0) {
        wallResults = calculateMultipleWalls(
          input.individualWalls,
          input.roomDimensions.height,
          input.includeCeiling
        );
        addNote(
          'results:notes.individualWallsMode',
          `Individual walls mode with ${input.individualWalls.length} walls`,
          { count: input.individualWalls.length },
        );
      }
      break;
  }

  // --- BOM SMOOTHING (pre-BOM, at WallResult/CeilingResult level) ---
  // Smoothing consolidates orphan SKUs into popular panels BEFORE building
  // surface breakdowns and BOM, so both PPG and BOM reflect the changes.
  const effectiveSmoothingConfig: BOMSmoothingConfig = smoothingConfig ?? {
    enabled: true,
    orphanThreshold: DEFAULT_ORPHAN_THRESHOLD,
    maxWasteIncreasePercent: DEFAULT_MAX_WASTE_INCREASE_PERCENT,
  };

  if (effectiveSmoothingConfig.enabled) {
    const smoothingResult = smoothResults(wallResults, ceilingResult, effectiveSmoothingConfig);
    wallResults = smoothingResult.wallResults;
    if (smoothingResult.ceilingResult !== null) {
      ceilingResult = smoothingResult.ceilingResult;
    }
  }

  // --- BUILD SURFACE BREAKDOWNS (from possibly smoothed results) ---
  let ceilingSurfaceBreakdown: SurfaceBreakdown | undefined;
  let ceilingZoneBreakdowns: SurfaceBreakdown[] | undefined;
  const wallSurfaceBreakdowns: SurfaceBreakdown[] = [];

  // Ceiling surface breakdown
  if (ceilingResult && input.ceilingConfig?.enabled) {
    const ceilingMaterials: SurfaceMaterials = {
      panels: ceilingResult.panels,
      trims: [...ceilingResult.hTrims, ...ceilingResult.jTrims],
    };

    ceilingSurfaceBreakdown = {
      surfaceId: 'ceiling',
      surfaceName: 'Ceiling',
      surfaceNameKey: 'results:surfaces.ceiling',
      enabled: true,
      config: input.ceilingConfig,
      materials: ceilingMaterials,
    };

    // Build per-zone breakdowns for multi-zone ceilings
    const zones = input.ceilingZones;
    if (zones && zones.length > 1) {
      ceilingZoneBreakdowns = zones.map((zone, idx) => {
        const zoneResult = calculateCeilingZone(zone.length, zone.width, zone.color, zone.name || `Zone ${idx + 1}`);
        return {
          surfaceId: `ceiling-zone-${idx}`,
          surfaceName: zone.name || `Zone ${idx + 1}`,
          // Only the generated fallback is translatable — a zone the user
          // named keeps that name in every language.
          ...(zone.name ? {} : {
            surfaceNameKey: 'results:surfaces.zone',
            surfaceNameParams: { number: idx + 1 },
          }),
          enabled: true,
          config: {
            ...input.ceilingConfig!,
            color: zone.color,
            // Store zone dimensions for display
            zoneLength: zone.length,
            zoneWidth: zone.width,
          } as any,
          materials: {
            panels: zoneResult.panels,
            trims: [...zoneResult.hTrims, ...zoneResult.jTrims],
          },
        } as SurfaceBreakdown;
      });
    }
  }

  // Wall surface breakdowns
  if (input.calculationMode === 'FullRoom' && input.fullRoomWallConfig) {
    // Full-room wall names are generated, so they carry an i18n key. (In
    // IndividualWalls mode the names are the user's own and stay verbatim.)
    const wallNames = ['Wall A (Length)', 'Wall B (Width)', 'Wall C (Length)', 'Wall D (Width)'];
    const wallNameKeys = [
      'results:surfaces.wallALength', 'results:surfaces.wallBWidth',
      'results:surfaces.wallCLength', 'results:surfaces.wallDWidth',
    ];
    wallResults.forEach((result, index) => {
      const wallConfig = {
        wallId: `wall-${index + 1}`,
        name: wallNames[index] || `Wall ${index + 1}`,
        enabled: true,
        length: index % 2 === 0 ? input.roomDimensions.length : input.roomDimensions.width,
        height: input.roomDimensions.height,
        color: input.fullRoomWallConfig!.wallColor,
        orientation: input.fullRoomWallConfig!.wallOrientation,
        slatWall: input.fullRoomWallConfig!.slatWall,
        garage: {
          ...input.fullRoomWallConfig!.garage,
          enabled:
            input.fullRoomWallConfig!.garage.enabled &&
            ((input.fullRoomWallConfig!.garage.onWallType === 'LengthWall' && index === 0) ||
              (input.fullRoomWallConfig!.garage.onWallType === 'WidthWall' && index === 1)),
        },
      };
      const breakdown = wallResultToSurfaceBreakdown(result, wallConfig);
      if (wallNameKeys[index]) breakdown.surfaceNameKey = wallNameKeys[index];
      wallSurfaceBreakdowns.push(breakdown);
    });
  } else if (input.calculationMode === 'IndividualWalls' && input.individualWalls) {
    input.individualWalls.forEach((wallConfig, index) => {
      if (wallResults[index]) {
        wallSurfaceBreakdowns.push(
          wallResultToSurfaceBreakdown(wallResults[index], wallConfig)
        );
      }
    });
  }

  // --- BUILD BILL OF MATERIALS ---
  // For FullRoom mode, pass configuration for legacy J-trim calculation
  const fullRoomTrimConfig: FullRoomTrimConfig | undefined =
    input.calculationMode === 'FullRoom' && input.fullRoomWallConfig
      ? {
          roomDimensions: input.roomDimensions,
          fullRoomWallConfig: input.fullRoomWallConfig,
        }
      : undefined;

  const billOfMaterials = buildBillOfMaterials(
    ceilingResult,
    wallResults,
    fullRoomTrimConfig
  );

  // --- CALCULATE COSTS ---
  const totals = calculateCostTotals(billOfMaterials);

  // Determine currency (default to USD, can be overridden by client-side detection)
  const currency: Currency = 'USD';

  // --- BUILD OUTPUT ---
  const output: EstimateOutput = {
    createdAt: new Date(),
    input,
    surfaces: {
      ceiling: ceilingSurfaceBreakdown,
      ceilingZones: ceilingZoneBreakdowns,
      walls: wallSurfaceBreakdowns,
    },
    billOfMaterials,
    totals,
    currency,
    notes: notes(),
    notesI18n,
  };

  return output;
}

/**
 * Quick estimate for ceiling only.
 *
 * @param length - Ceiling length in feet
 * @param width - Ceiling width in feet
 * @param color - Panel color
 * @returns Estimate output for ceiling only
 */
export function estimateCeilingOnly(
  length: number,
  width: number,
  color: 'White' | 'Gray' = 'White'
): EstimateOutput {
  const input: EstimateInput = {
    calculationMode: 'CeilingOnly',
    roomDimensions: {
      length,
      width,
      height: 8, // Default height (not used for ceiling-only)
    },
    includeCeiling: true,
    ceilingConfig: {
      enabled: true,
      color,
    },
  };

  return doEstimate(input);
}

/**
 * Quick estimate for full room with defaults.
 *
 * @param length - Room length in feet
 * @param width - Room width in feet
 * @param height - Room height in feet
 * @param options - Optional configuration overrides
 * @returns Estimate output for full room
 */
export function estimateFullRoom(
  length: number,
  width: number,
  height: number,
  options: {
    wallColor?: 'White' | 'Gray';
    wallOrientation?: 'Horizontal' | 'Vertical';
    includeCeiling?: boolean;
    ceilingColor?: 'White' | 'Gray';
    slatWallCoverage?: 0 | 25 | 50;
    slatWallColor?: 'White' | 'Gray';
  } = {}
): EstimateOutput {
  const {
    wallColor = 'White',
    wallOrientation = 'Horizontal',
    includeCeiling = true,
    ceilingColor = 'White',
    slatWallCoverage = 0,
    slatWallColor = 'Gray',
  } = options;

  // Calculate SlatWall rows from coverage percentage
  const slatWallHeight = height * (slatWallCoverage / 100);
  const numPanelRows = Math.round(slatWallHeight / 0.5); // 6 inches per row

  const input: EstimateInput = {
    calculationMode: 'FullRoom',
    roomDimensions: { length, width, height },
    includeCeiling,
    ceilingConfig: {
      enabled: includeCeiling,
      color: ceilingColor,
    },
    fullRoomWallConfig: {
      wallColor,
      wallOrientation,
      slatWall: {
        enabled: slatWallCoverage > 0,
        startHeight: slatWallCoverage > 0 ? Math.max(2, (height - slatWallHeight) / 2) : 0,
        numPanelRows,
        color: slatWallColor,
        useCapTrim: slatWallCoverage > 0,
      },
      garage: {
        enabled: false,
        width: 0,
        height: 0,
        numDoors: 0,
        onWallType: 'None',
      },
    },
  };

  return doEstimate(input);
}

/**
 * Get a summary of the estimate for quick display.
 *
 * @param output - Estimate output
 * @returns Summary object with key metrics
 */
export function getEstimateSummary(output: EstimateOutput): {
  totalPanels: number;
  totalTrims: number;
  totalSqft: number;
  totalScrews: number;
  costUSD: number;
  costCAD: number;
  surfaceCount: number;
} {
  const bom = output.billOfMaterials;

  const totalPanels =
    bom.wallAndCeilingBoard.reduce((sum, item) => sum + item.quantity, 0) +
    bom.slatwall.reduce((sum, item) => sum + item.quantity, 0);

  const totalTrims =
    bom.hTrims.reduce((sum, item) => sum + item.quantity, 0) +
    (bom.jTrimsSnapIn ?? []).reduce((sum, item) => sum + item.quantity, 0) +
    bom.jTrimsHalfInch.reduce((sum, item) => sum + item.quantity, 0) +
    bom.jTrimsThreeQuarterInch.reduce((sum, item) => sum + item.quantity, 0);

  const totalSqft =
    bom.wallAndCeilingBoard.reduce((sum, item) => sum + item.quantity * item.sqft, 0) +
    bom.slatwall.reduce((sum, item) => sum + item.quantity * item.sqft, 0);

  const surfaceCount =
    (output.surfaces.ceiling ? 1 : 0) + output.surfaces.walls.length;

  return {
    totalPanels,
    totalTrims,
    totalSqft: Math.round(totalSqft),
    totalScrews: bom.screws.total,
    costUSD: output.totals.totalCostUSD,
    costCAD: output.totals.totalCostCAD,
    surfaceCount,
  };
}

// Re-export all modules for convenience
export * from './constants';
export * from './dimensions';
export * from './panels';
export * from './slatwall';
export * from './trims';
export * from './screws';
export * from './ceiling';
export * from './walls';
export * from './consolidation';
export * from './validation';
export * from './smoothing';

export default {
  doEstimate,
  estimateCeilingOnly,
  estimateFullRoom,
  getEstimateSummary,
};
